import { useState } from "react";

import { SortOrder } from "@bridgesplit/utils";

import {
    ApiPaginationProps,
    ManagedPaginationProps,
    PaginationController,
    PaginationProps,
    TableColumn,
    TableKey,
    TableRow
} from "./types";

export function splitPaginationProps<T>(paginationProps: PaginationProps) {
    const managedPaginationProps =
        paginationProps?.type === "managed" ? (paginationProps as ManagedPaginationProps<T>) : undefined;
    const apiPaginationProps = paginationProps?.type === "api" ? (paginationProps as ApiPaginationProps) : undefined;
    return { apiPaginationProps, managedPaginationProps };
}

/** Self-managed pagination/sort state used by managed-mode tables. */
export function useTableController(paginationProps: PaginationProps): PaginationController {
    const [order, setOrder] = useState<SortOrder>(paginationProps.defaultOrder ?? SortOrder.Desc);
    const [orderBy, setOrderBy] = useState<string>(paginationProps.defaultSortKey ?? "");
    const [page, setPage] = useState<number>(0);

    const resetPage = () => setPage(0);

    return { order, setOrder, orderBy, setOrderBy, page, setPage, resetPage };
}

/**
 * Total-ordering comparator for sortable column values. Handles `undefined` (sorts
 * last), picks numeric vs. text strategy from the column's `sortType` instead of
 * guessing per row pair, and treats `NaN` consistently.
 */
function compareSortable(
    a: TableKey | undefined,
    b: TableKey | undefined,
    dir: SortOrder,
    type: "numeric" | "text"
): number {
    if (a === undefined && b === undefined) return 0;
    if (a === undefined) return 1;
    if (b === undefined) return -1;

    if (type === "numeric") {
        const na = typeof a === "number" ? a : Number(a);
        const nb = typeof b === "number" ? b : Number(b);
        const aNaN = Number.isNaN(na);
        const bNaN = Number.isNaN(nb);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1;
        if (bNaN) return -1;
        return dir === SortOrder.Desc ? nb - na : na - nb;
    }

    return dir === SortOrder.Desc ? String(b).localeCompare(String(a)) : String(a).localeCompare(String(b));
}

export function sortRows<T>(props: {
    rows: TableRow<T>[];
    columns: TableColumn<T>[];
    orderBy: string;
    order: SortOrder;
}) {
    const column = props.columns.find((c) => c.dataIndex === props.orderBy);
    const sortByFunction = column?.sortBy;

    if (!sortByFunction || !props.rows.length) {
        return props.rows;
    }

    const sortType = column?.sortType ?? "numeric";

    return [...props.rows].sort((a, b) => compareSortable(sortByFunction(a), sortByFunction(b), props.order, sortType));
}
